// path: frontend/src/pages/Landing.tsx
import { useNavigate } from "react-router-dom";
import { SEO } from "../components/Seo";
import { EducationalStructuredData } from "../components/StructuredData";

export default function Landing() {
  const navigate = useNavigate();

  return (
    <>
      <SEO
        title="AutoQuiz AI — AI Quiz Video Generator"
        description="Turn any medical topic into a quiz video for TikTok or YouTube in one click. AI generates questions, voiceover, and video automatically. For educational purposes only."
        keywords="AI quiz generator, quiz video generator, faceless video creation, TikTok quiz videos, YouTube quiz videos, educational video generator"
        canonical="https://autoquizai.com"
        type="website"
      />
      <EducationalStructuredData />

      <div style={{ minHeight: "100vh", background: "#080b12", color: "#f0f4ff", fontFamily: "Inter, sans-serif" }}>

        {/* Nav */}
        <nav style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "20px 40px", borderBottom: "1px solid #1a1a24", position: "sticky", top: 0, background: "rgba(8,11,18,0.95)", backdropFilter: "blur(10px)", zIndex: 100 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 36, height: 36, background: "#3d8ef8", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 900, color: "#fff", fontSize: 13 }}>AQ</div>
            <span style={{ fontWeight: 800, fontSize: 18 }}>Auto<span style={{ color: "#3d8ef8" }}>Quiz</span> AI</span>
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={() => navigate("/login")} style={{ background: "transparent", border: "1px solid #232330", color: "#f0f4ff", padding: "8px 20px", borderRadius: 8, cursor: "pointer", fontWeight: 600, fontSize: 14 }}>Sign in</button>
            <button onClick={() => navigate("/login?signup=1")} style={{ background: "#3d8ef8", border: "none", color: "#fff", padding: "8px 20px", borderRadius: 8, cursor: "pointer", fontWeight: 800, fontSize: 14 }}>Start free →</button>
          </div>
        </nav>

        {/* Hero */}
        <div style={{ textAlign: "center", padding: "80px 20px 60px", maxWidth: 860, margin: "0 auto" }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "rgba(61,142,248,0.1)", border: "1px solid rgba(61,142,248,0.3)", borderRadius: 20, padding: "6px 16px", fontSize: 13, color: "#3d8ef8", marginBottom: 28, fontWeight: 700 }}>
            ⚡ AI-Powered Quiz Video Generator
          </div>
          <h1 style={{ fontSize: "clamp(34px, 6vw, 72px)", fontWeight: 900, lineHeight: 1.1, marginBottom: 24 }}>
            Turn any topic into<br /><span style={{ color: "#3d8ef8" }}>quiz videos in one click</span>
          </h1>
          <p style={{ fontSize: 17, color: "#8b9cc8", maxWidth: 540, margin: "0 auto 40px", lineHeight: 1.6 }}>
            An AI tool that generates questions, voiceover, countdown, and video — ready to post on TikTok or YouTube. Designed to support medical education content creation.
          </p>
          <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap", marginBottom: 16 }}>
            <button onClick={() => navigate("/login?signup=1")} style={{ background: "#3d8ef8", border: "none", color: "#fff", padding: "14px 32px", borderRadius: 10, cursor: "pointer", fontWeight: 800, fontSize: 16 }}>
              🚀 Start creating free
            </button>
            <button onClick={() => navigate("/credits")} style={{ background: "transparent", border: "1px solid #1e2a42", color: "#f0f4ff", padding: "14px 32px", borderRadius: 10, cursor: "pointer", fontWeight: 600, fontSize: 16 }}>
              View credits
            </button>
          </div>
          <p style={{ fontSize: 12, color: "#4a5a80" }}>Quiz generation is free · Final videos use credits</p>
        </div>

        {/* Disclaimer bar */}
        <div style={{ background: "rgba(124,58,237,0.08)", borderTop: "1px solid rgba(124,58,237,0.2)", borderBottom: "1px solid rgba(124,58,237,0.2)", padding: "12px 20px", textAlign: "center", fontSize: 12, color: "#6b7280" }}>
          ⚠️ AI-generated content may contain errors. Review quiz content before publishing or relying on it.
        </div>

        {/* Topics */}
        <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: 10, padding: "40px 20px", maxWidth: 800, margin: "0 auto" }}>
          {["Education", "Trivia", "History", "Science", "Language", "Business", "Technology", "Entertainment", "Any Topic"].map(t => (
            <span key={t} style={{ background: "#0e1420", border: "1px solid #1e2a42", borderRadius: 20, padding: "6px 16px", fontSize: 13, fontWeight: 600, color: "#8b9cc8" }}>{t}</span>
          ))}
        </div>

        {/* Features */}
        <div style={{ padding: "60px 20px", maxWidth: 1060, margin: "0 auto", borderTop: "1px solid #1e2a42" }}>
          <h2 style={{ textAlign: "center", fontSize: "clamp(26px, 4vw, 40px)", fontWeight: 900, marginBottom: 12 }}>What AutoQuiz AI does</h2>
          <p style={{ textAlign: "center", color: "#8b9cc8", fontSize: 15, marginBottom: 44 }}>AI handles everything — from questions to the final video</p>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 20 }}>
            {[
              { icon: "🧪", title: "AI Question Generation", desc: "Generates high-yield multiple choice questions on any medical topic, with explanations." },
              { icon: "🎙️", title: "AI Voiceover", desc: "Natural-sounding AI voices read questions and answers in English or French." },
              { icon: "⏱️", title: "Countdown Timer", desc: "Automatic countdown gives viewers time to think before the answer is revealed." },
              { icon: "🎬", title: "Auto Video Render", desc: "FFmpeg renders the full video with background, text, and audio automatically." },
              { icon: "📱", title: "TikTok & YouTube", desc: "Export in 9:16 for TikTok/Shorts or 16:9 for YouTube — optimized for each platform." },
              { icon: "🌍", title: "English & French", desc: "Generate quiz videos in English or French with native-sounding AI voices." },
            ].map((f, i) => (
              <div key={i} style={{ background: "#0e1420", border: "1px solid #1e2a42", borderRadius: 12, padding: "24px" }}>
                <div style={{ fontSize: 26, marginBottom: 12 }}>{f.icon}</div>
                <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 8 }}>{f.title}</h3>
                <p style={{ fontSize: 13, color: "#8b9cc8", lineHeight: 1.5 }}>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* How it works */}
        <div style={{ padding: "60px 20px", maxWidth: 1060, margin: "0 auto", borderTop: "1px solid #1e2a42" }}>
          <h2 style={{ textAlign: "center", fontSize: "clamp(26px, 4vw, 40px)", fontWeight: 900, marginBottom: 12 }}>How it works</h2>
          <p style={{ textAlign: "center", color: "#8b9cc8", fontSize: 15, marginBottom: 44 }}>From topic to video in under 2 minutes</p>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 24 }}>
            {[
              { n: "1", title: "Enter a topic", desc: "Type any medical topic or let AI pick one for you." },
              { n: "2", title: "AI generates quiz", desc: "AI writes high-yield questions with answers and explanations." },
              { n: "3", title: "Preview & edit", desc: "Review questions, swap answers, adjust settings before rendering." },
              { n: "4", title: "Download & post", desc: "Download your video and post directly to TikTok or YouTube." },
            ].map((s, i) => (
              <div key={i} style={{ textAlign: "center", padding: 20 }}>
                <div style={{ width: 44, height: 44, background: "rgba(61,142,248,0.1)", border: "1px solid rgba(61,142,248,0.3)", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 900, fontSize: 16, color: "#3d8ef8", margin: "0 auto 14px" }}>{s.n}</div>
                <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 6 }}>{s.title}</h3>
                <p style={{ fontSize: 13, color: "#8b9cc8" }}>{s.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Credits */}
        <div
          style={{
            padding: "60px 20px",
            maxWidth: 760,
            margin: "0 auto",
            borderTop: "1px solid #1e2a42",
          }}
          id="credits"
        >
          <h2
            style={{
              textAlign: "center",
              fontSize: "clamp(26px, 4vw, 40px)",
              fontWeight: 900,
              marginBottom: 12,
            }}
          >
            Simple credits
          </h2>

          <p
            style={{
              textAlign: "center",
              color: "#8b9cc8",
              fontSize: 15,
              marginBottom: 32,
            }}
          >
            No subscriptions. Buy more credits only when you need them.
          </p>

          <div
            style={{
              background: "#0e1420",
              border: "1px solid #3d8ef8",
              borderRadius: 12,
              padding: 28,
            }}
          >
            <ul
              style={{
                color: "#8b9cc8",
                lineHeight: 1.9,
                fontSize: 14,
              }}
            >
              <li>New accounts receive 10 credits</li>
              <li>Quiz generation and previews are free</li>
              <li>Final video generation costs 5 credits</li>
              <li>Credits do not reset monthly</li>
              <li>Buy 50-credit packs whenever you need more</li>
            </ul>

            <button
              onClick={() => navigate("/login?signup=1")}
              style={{
                width: "100%",
                marginTop: 18,
                padding: 12,
                borderRadius: 8,
                fontWeight: 800,
                cursor: "pointer",
                background: "#3d8ef8",
                color: "#fff",
                border: "none",
              }}
            >
              Start with 10 credits →
            </button>
          </div>
        </div>

        {/* CTA */}
        <div style={{ textAlign: "center", padding: "70px 20px", borderTop: "1px solid #1e2a42" }}>
          <h2 style={{ fontSize: "clamp(26px, 4vw, 42px)", fontWeight: 900, marginBottom: 14 }}>Ready to create your first quiz video?</h2>
          <p style={{ color: "#8b9cc8", fontSize: 15, marginBottom: 32, maxWidth: 480, margin: "0 auto 32px" }}>Create your quiz for free and use credits only when you generate the final video.</p>
          <button onClick={() => navigate("/login?signup=1")} style={{ background: "#3d8ef8", border: "none", color: "#fff", padding: "16px 36px", borderRadius: 10, cursor: "pointer", fontWeight: 800, fontSize: 16 }}>
            🚀 Start creating free
          </button>
        </div>

        {/* Footer */}
        <div style={{ textAlign: "center", padding: "28px 20px", borderTop: "1px solid #1e2a42", fontSize: 12, color: "#4a5a80" }}>
          <div style={{ marginBottom: 10, display: "flex", justifyContent: "center", flexWrap: "wrap", gap: "0 20px" }}>
            <a href="/privacy" style={{ color: "#4a5a80" }}>Privacy Policy</a>
            <a href="/terms" style={{ color: "#4a5a80" }}>Terms of Service</a>
            <a href="/faq" style={{ color: "#4a5a80" }}>FAQ</a>
            <a href="https://findtheideas.com" target="_blank" rel="noopener noreferrer" style={{ color: "#4a5a80" }}>FindTheIdeas</a>
            <a href="https://locuvo.findtheideas.com" target="_blank" rel="noopener noreferrer" style={{ color: "#4a5a80" }}>Locuvo</a>
            <a href="https://www.amazon.com/dp/B0H3GXNLJX" target="_blank" rel="noopener noreferrer" style={{ color: "#4a5a80" }}>Revenue Cycle Management in Medical Billing</a>
            <a href="https://www.amazon.com/dp/B0HF42QPDK" target="_blank" rel="noopener noreferrer" style={{ color: "#4a5a80" }}>Certified Billing and Coding Specialist Exam Study Guide 2026–2027</a>
            <a href="https://www.amazon.com/dp/B0HF3VDYKC" target="_blank" rel="noopener noreferrer" style={{ color: "#4a5a80" }}>Certified Billing and Coding Specialist Practice Tests 2026–2027</a>
            <a href="https://www.amazon.com/dp/B0HHK2KRY2" target="_blank" rel="noopener noreferrer" style={{ color: "#4a5a80" }}>Medical Terminology for Allied Health Sciences</a>
          </div>
          <div>© {new Date().getFullYear()} MC SANTE PLUS SERVICES LLC · AutoQuiz AI</div>
          <div style={{ maxWidth: 680, margin: "8px auto 0", fontSize: 11, lineHeight: 1.5 }}>
            AI-generated content may contain errors. Review questions, answers, and explanations before publishing or relying on them.
          </div>
        </div>

      </div>
    </>
  );
}
